import { define } from './define';

// Using Postbay from an AI assistant (MCP): the settings section, the person's own connections, the consent page, and how the activity
// says something came through one. Spanish first; English must have the same keys (TypeScript checks it).
export const assistants = define({
  es: {
    'settings.tab.assistants': 'Asistentes (MCP)',
    'settings.tabDesc.assistants': 'Usar Postbay desde Claude u otro asistente: qué puede hacer y quién lo ha conectado',

    'assistants.server.title': 'Dirección del servidor',
    'assistants.server.hint': 'En claude.ai o Claude Desktop: Ajustes → Conectores → Añadir conector personalizado, y pega esta dirección. Cada persona entra con su propia cuenta; no hay claves que repartir.',
    'assistants.server.code': 'En Claude Code',
    'assistants.server.copied': 'Dirección copiada',

    'assistants.approve.title': 'Decidir desde un asistente',
    'assistants.approve.label': 'Permitir aprobar desde asistentes (MCP)',
    'assistants.approve.hint': 'También pedir cambios. Solo para quien ya puede hacerlo aquí, y el asistente tiene que repetir la pieza, la versión y su huella antes de decidir.',
    'assistants.approve.on': 'Ya se puede aprobar desde asistentes',
    'assistants.approve.off': 'Ya no se puede aprobar desde asistentes',

    'assistants.connections.title': 'Conexiones',
    'assistants.connections.empty': 'Nadie ha conectado un asistente a esta marca.',
    'assistants.connections.since': 'conectado {when}',
    'assistants.connections.used': 'usado {when}',
    'assistants.connections.otherBrands': { one: 'y 1 marca más', other: 'y {count} marcas más' },
    'assistants.connections.answersTo': 'Responde a {host}',
    'assistants.disconnect': 'Desconectar',
    'assistants.actionsOf': 'Acciones de {name}',
    'assistants.disconnect.title': '¿Desconectar {client} de {person}?',
    'assistants.disconnect.text': '{client} deja de poder usar esta marca en nombre de {person} ahora mismo. Para volver, esa persona tendrá que conectarlo otra vez.',
    'assistants.disconnect.done': 'Desconectado',

    'assistants.mine.title': 'Asistentes',
    'assistants.mine.hint': 'Claude u otro asistente que usa Postbay con tu cuenta y tu rol en cada marca. Lo que hace queda registrado como tuyo, vía ese asistente.',
    'assistants.mine.empty': 'Ningún asistente conectado.',
    'assistants.mine.in': 'En {brands}',
    'assistants.mine.noBrands': 'Sin marcas activas',
    'assistants.mine.disconnectTitle': '¿Desconectar {client}?',
    'assistants.mine.disconnectText': '{client} deja de poder usar Postbay con tu cuenta ahora mismo.',

    'consent.title': 'Conectar {client}',
    'consent.lead': 'Usará Postbay con tu cuenta.',
    'consent.answersTo': 'Responde a',
    'consent.local': 'Es una dirección de este ordenador: cualquier programa abierto en él podría usarla. Sigue solo si acabas de conectar Claude Code o una app de escritorio.',
    'consent.brands': 'Marcas',
    'consent.as': '{role}',
    'consent.canApprove': 'puede aprobar',
    'consent.acts': 'Actuará con tu rol en cada marca que elijas: verá, comentará, subirá y programará lo mismo que tú. Todo queda registrado como tuyo, vía {client}.',
    'consent.allow': 'Permitir',
    'consent.deny': 'Cancelar',
    'consent.back': 'Volviendo a {client}…',
    'consent.later': 'Puedes desconectarlo cuando quieras en Tu cuenta.',
    'consent.failed': 'No se puede conectar',
    'consent.error.invalid_client': 'Este asistente no está registrado en Postbay. Vuelve a añadir el conector desde su aplicación.',
    'consent.error.invalid_redirect_uri': 'El asistente pidió volver a una dirección que no registró. Por seguridad, no se sigue.',
    'consent.error.missing': 'Falta la solicitud de acceso. Vuelve a conectar el asistente desde su aplicación.',

    'home.activity.via': 'vía {client}',
  },
  en: {
    'settings.tab.assistants': 'Assistants (MCP)',
    'settings.tabDesc.assistants': 'Use Postbay from Claude or another assistant: what it may do and who connected one',

    'assistants.server.title': 'Server address',
    'assistants.server.hint': 'In claude.ai or Claude Desktop: Settings → Connectors → Add custom connector, and paste this address. Everyone signs in with their own account; there are no keys to hand out.',
    'assistants.server.code': 'In Claude Code',
    'assistants.server.copied': 'Address copied',

    'assistants.approve.title': 'Deciding from an assistant',
    'assistants.approve.label': 'Allow approving from assistants (MCP)',
    'assistants.approve.hint': 'Requesting changes too. Only for people who can already do it here, and the assistant has to repeat the piece, the version and its fingerprint before deciding.',
    'assistants.approve.on': 'Approving from assistants is on',
    'assistants.approve.off': 'Approving from assistants is off',

    'assistants.connections.title': 'Connections',
    'assistants.connections.empty': 'Nobody has connected an assistant to this brand.',
    'assistants.connections.since': 'connected {when}',
    'assistants.connections.used': 'used {when}',
    'assistants.connections.otherBrands': { one: 'and 1 more brand', other: 'and {count} more brands' },
    'assistants.connections.answersTo': 'Answers to {host}',
    'assistants.disconnect': 'Disconnect',
    'assistants.actionsOf': 'Actions for {name}',
    'assistants.disconnect.title': 'Disconnect {client} from {person}?',
    'assistants.disconnect.text': '{client} can no longer use this brand on behalf of {person}, starting now. To come back, they will have to connect it again.',
    'assistants.disconnect.done': 'Disconnected',

    'assistants.mine.title': 'Assistants',
    'assistants.mine.hint': 'Claude or another assistant that uses Postbay with your account and your role in each brand. What it does is recorded as yours, via that assistant.',
    'assistants.mine.empty': 'No assistant connected.',
    'assistants.mine.in': 'In {brands}',
    'assistants.mine.noBrands': 'No active brands',
    'assistants.mine.disconnectTitle': 'Disconnect {client}?',
    'assistants.mine.disconnectText': '{client} can no longer use Postbay with your account, starting now.',

    'consent.title': 'Connect {client}',
    'consent.lead': 'It will use Postbay with your account.',
    'consent.answersTo': 'Answers to',
    'consent.local': 'This is an address on this computer: any program open on it could use it. Go on only if you have just connected Claude Code or a desktop app.',
    'consent.brands': 'Brands',
    'consent.as': '{role}',
    'consent.canApprove': 'can approve',
    'consent.acts': 'It will act with your role in each brand you choose: it will see, comment, upload and schedule what you can. Everything is recorded as yours, via {client}.',
    'consent.allow': 'Allow',
    'consent.deny': 'Cancel',
    'consent.back': 'Going back to {client}…',
    'consent.later': 'You can disconnect it at any time in Your account.',
    'consent.failed': 'Cannot connect',
    'consent.error.invalid_client': 'This assistant is not registered with Postbay. Add the connector again from its own app.',
    'consent.error.invalid_redirect_uri': 'The assistant asked to return to an address it did not register. For safety, this does not go on.',
    'consent.error.missing': 'The access request is missing. Connect the assistant again from its own app.',

    'home.activity.via': 'via {client}',
  },
});
